import {
  ACCOUNT_TYPE_DEFINITIONS,
  CONTACT_ROLE_DEFINITIONS,
  DISPOSITION_DEFINITIONS,
  GC_STATUS_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  PROJECT_STAGE_DEFINITIONS,
  ROLE_FAMILY_DEFINITIONS,
  SIGNAL_TYPE_DEFINITIONS,
} from '@parcel/types';
import type { WhereField } from '../types.js';
import {
  PARKING_TYPE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  REVIEW_PATH_OPTIONS,
  STRUCTURE_OPTIONS,
} from '../vocab.js';
import { fieldsIn, workspaceFields } from './shared.js';

const stages = PROJECT_STAGE_DEFINITIONS.map((d) => ({
  value: d.value,
  label: d.label,
  description: d.description,
}));
const p = fieldsIn('project');
const f = fieldsIn('firm', 'firm');
const c = fieldsIn('contact', 'contact');
const s = fieldsIn('signals', 'signal');

export const PROJECT_FIELDS: readonly WhereField[] = [
  p('name', 'Name', 'text', 'Project name.'),
  p('address', 'Address', 'text', 'Street address as filed.'),
  p('city', 'City', 'choice', 'City of the project.', { source: 'cities' }),
  p('neighborhood', 'Neighborhood', 'choice', 'Neighborhood, where the city reports one.', {
    source: 'neighborhoods',
  }),
  p('state', 'State', 'text', 'Two-letter state code.'),
  p('zip', 'ZIP', 'text', 'ZIP code.'),
  p('stage', 'Stage', 'choice', 'Development stage.', { options: stages }),
  p('status', 'Status', 'choice', 'Whether the project is active or reached an outcome.', {
    options: DISPOSITION_DEFINITIONS,
  }),
  p('use', 'Use', 'choice', 'Primary use.', { options: PRIMARY_USE_DEFINITIONS }),
  p(
    'project_type',
    'Project type',
    'choice',
    'Single building, master plan or phased development.',
    { options: PROJECT_TYPE_OPTIONS },
  ),
  p('units', 'Units', 'number', 'Dwelling units.', { unit: 'units' }),
  p('sqft', 'Square footage', 'number', 'Gross floor area.', { unit: 'sqft' }),
  p('lot_size', 'Lot size', 'number', 'Lot area.', { unit: 'sqft' }),
  p('stories', 'Stories', 'number', 'Stories above grade.'),
  p('height', 'Height', 'number', 'Building height.', { unit: 'ft' }),
  p('cost', 'Construction cost', 'number', 'Construction cost in US dollars.', { unit: 'usd' }),
  p('year_completed', 'Year completed', 'number', 'Year it completed or is expected to.', {
    unit: 'year',
  }),
  p('parking_spaces', 'Parking spaces', 'number', 'Parking spaces.'),
  p('parking_type', 'Parking type', 'choice', 'Garage, underground or surface.', {
    options: PARKING_TYPE_OPTIONS,
  }),
  p('hotel_rooms', 'Hotel rooms', 'number', 'Hotel keys.'),
  p('beds', 'Beds', 'number', 'Beds in dorms or assisted living.'),
  p('jobs', 'Jobs', 'number', 'Jobs reported, or estimated when none is reported.'),
  p('mixed_use', 'Mixed use', 'yes_no', 'A mix of residential and commercial uses.'),
  p('senior', 'Senior housing', 'yes_no', 'Age-restricted housing.'),
  p('as_of_right', 'As of right', 'yes_no', 'Needs no discretionary zoning relief.'),
  p('phased', 'Phased', 'yes_no', 'Built in phases.'),
  p('redevelopment', 'Redevelopment', 'yes_no', 'Replaces or reuses existing buildings.'),
  p('stalled', 'Stalled', 'yes_no', 'The project has stalled.'),
  p('review_path', 'Review path', 'choice', 'Zoning and review paths.', {
    options: REVIEW_PATH_OPTIONS,
  }),
  p('structure', 'Structure', 'choice', 'How the building is built.', {
    options: STRUCTURE_OPTIONS,
  }),
  p('gc', 'General contractor', 'choice', 'Whether a general contractor is named.', {
    options: GC_STATUS_DEFINITIONS,
  }),
  p('signals_count', 'Number of signals', 'number', 'Signals within your plan history.', {
    unit: 'count',
  }),
  p('firms_count', 'Number of firms', 'number', 'Firms linked to the project.', { unit: 'count' }),
  p('contacts_count', 'Number of contacts', 'number', 'People linked to the project.', {
    unit: 'count',
  }),
  p('last_signal', 'Last signal', 'date', 'Date of the newest signal.'),
  p('added', 'Added to Parcel', 'date', 'When the project entered Parcel.'),

  f('name', 'Name', 'text', 'Name of a firm on the project.'),
  f('type', 'Type', 'choice', 'What the firm does.', { options: ACCOUNT_TYPE_DEFINITIONS }),
  f('role', 'Role on the project', 'choice', "The firm's role on this project.", {
    options: PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  }),
  f('city', 'City', 'choice', "City of the firm's office.", { source: 'firm_cities' }),
  f('score', 'ICP Score', 'score', "The firm's ICP Score, 0 to 100."),

  c('title', 'Title', 'text', 'Job title of someone on the project.'),
  c('persona', 'Persona', 'choice', 'Role family the title maps to.', {
    options: ROLE_FAMILY_DEFINITIONS,
  }),
  c('role', 'Role on the project', 'choice', 'Their role on this project.', {
    options: CONTACT_ROLE_DEFINITIONS,
  }),

  s('type', 'Type', 'choice', 'Kind of event.', { options: SIGNAL_TYPE_DEFINITIONS }),
  s('filed', 'Filed date', 'date', 'When the event happened.'),
  s('summary', 'Summary', 'text', 'One-line summary of the event.'),

  fieldsIn('scores')('score', 'Atlas Score', 'score', "This workspace's Atlas Score, 0 to 100."),
  ...workspaceFields('project'),
];
